import { getContext } from '@fiverr-private/fiverr_context';
import { init, renderEntries } from '@fiverr-private/user_session';
import { sessionStorage } from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import cookie from '../../../lib/cookie';
import stats from '../../../lib/stats';
import { EVENT_TYPES, trackEvent } from './events';
import {
    AUTO_SHOW_TRIGGER,
    FALLBACK_ROUTES,
    HANDLER_TYPES,
    QUERY_PARAMS,
    REDIRECT_COOKIE,
    REDIRECT_EXPIRES_IN,
    SIGN_IN_SELECTOR,
    SIGN_UP_AUTO_SHOWN_KEY,
    SIGN_UP_SELECTOR
} from './constants';

// **THIS MODULE IS TEMPORARY AND SHOULD BE REMOVED ONCE HEADER USES THE SESSION PACKAGE DIRECTLY**

/**
 * Toggles (set or removes) redirect cookie according to given redirect url
 * @param {String} redirectUrl The redirect url to set if given.
 */
const toggleRedirectCookie = (redirectUrl) => {
    if (!redirectUrl) {
        cookie.remove(REDIRECT_COOKIE);
        return;
    }

    cookie.set(REDIRECT_COOKIE, redirectUrl, REDIRECT_EXPIRES_IN);
};

/**
 * Gets requested parameter from current window SearchParams.
 * @param {String} paramName - The param name to get.
 * @return {String}
 */
const getQueryParams = (paramName) => new URLSearchParams(window.location.search)
    .get(paramName);

/**
 * Creates `UserSession` sign up modal options.
 * @return {Object}
 */
const getSignUpOptions = (optionsOverrides = {}) => {
    const referralEmail = getQueryParams(QUERY_PARAMS.REFERRAL_EMAIL);

    const referralOptions = referralEmail ? {
        isSticky: true,
        initialValues: {
            email: decodeURIComponent(referralEmail)
        }
    } : {};

    return {
        ...referralOptions,
        ...optionsOverrides
    };
};

const triggerSignInModal = () => {
    triggerModal({
        trigger: window.openSignInModal,
        handlerType: HANDLER_TYPES.SIGN_IN
    });
};

const triggerSignUpModal = (optionsOverrides = {}) => {
    triggerModal({
        trigger: window.openSignUpModal,
        options: getSignUpOptions(optionsOverrides),
        handlerType: HANDLER_TYPES.SIGN_UP
    });
};

/**
 * Triggers the signing modal. The user would be redirected to the fallback route
 * in case the trigger api method is not available or on mobile devices.
 *
 * @param {Function} trigger - The modal trigger method.
 * @param {Object} options - The options to be passed to the trigger method.
 * @param {String} handlerType - The signing flow handler type.
 */
const triggerModal = ({ trigger, options = {}, handlerType }) => {
    const { isTouch } = getContext();

    if (isTouch && !options.forceModal) {
        fallbackRedirect(handlerType);
        return;
    }

    if (!trigger) {
        stats.count(`signing_modal_trigger_failed.${handlerType.toLowerCase()}`);
        fallbackRedirect(handlerType);
        return;
    }

    trigger(options);
};

const fallbackRedirect = (handlerType) => {
    const fallbackRoute = pathfinder(FALLBACK_ROUTES[handlerType]);

    window.location.assign(fallbackRoute);
};

/**
 * @typedef UserSessionListener
 * @property {Function} action - The action to be triggered.
 * @property {String} eventType - The event type related to be tracked.
 */

/**
 * A map of element selectors, and their respective handler exported from `user_session` package.
 * @type {Object<UserSessionListener>}
 * @see https://github.com/fiverr/user_session_package#external-modal-triggers
 */
const LISTENER_MAP = {
    [HANDLER_TYPES.SIGN_IN]: {
        action: triggerSignInModal,
        eventType: EVENT_TYPES.SIGN_IN
    },
    [HANDLER_TYPES.SIGN_UP]: {
        action: triggerSignUpModal,
        eventType: EVENT_TYPES.SIGN_UP
    }
};

/**
 * Triggers user session click handler.
 * @param event - The html event triggered.
 * @param handlerType - The handler type (one of HANDLER_TYPES ).
 */
const userSessionClickHandler = (event, handlerType) => {
    const { target } = event;
    const {
        sourceComponent,
        redirectUrl,
        eventType: customBigQueryEvent
    } = target.dataset;

    const { action, eventType } = LISTENER_MAP[handlerType];

    event.preventDefault();

    trackEvent({ eventType, customBigQueryEvent, sourceComponent });
    toggleRedirectCookie(redirectUrl);

    action();
};

/**
 * Binds listeners to all user session elements on the page.
 */
const bindListeners = () => {
    document.querySelectorAll(SIGN_IN_SELECTOR)
        .forEach((el) => el.addEventListener('click', (event) => {
            userSessionClickHandler(event, HANDLER_TYPES.SIGN_IN);
        }));

    document.querySelectorAll(SIGN_UP_SELECTOR)
        .forEach((el) => el.addEventListener('click', (event) => {
            userSessionClickHandler(event, HANDLER_TYPES.SIGN_UP);
        }));
};

const handleQueryParamTriggers = () => {
    if (getQueryParams(QUERY_PARAMS.SHOW_JOIN)) {
        sessionStorage.set(SIGN_UP_AUTO_SHOWN_KEY, true);

        triggerSignUpModal({
            triggerType: AUTO_SHOW_TRIGGER,
            forceModal: true
        });
    }
};

export const onLoad = () => {
    renderEntries();
    bindListeners(); // TODO: Migrate this logic to user session init
    handleQueryParamTriggers();
    init();
};
